import { useState, type FormEvent, type JSX } from 'react'
import { api } from '../../api/client.js'

interface Props {
  passwordChangeToken: string
  /** The temporary password just typed at sign-in; sent as proof of the current password. */
  currentPassword: string
  onDone: () => void
  onClose: () => void
}

/** SE-07: shown instead of a session when the account's password is temporary. */
export default function ForcedPasswordChangeDialog({ passwordChangeToken, currentPassword, onDone, onClose }: Props): JSX.Element {
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    if (newPassword !== confirm) {
      setError('The two passwords do not match')
      return
    }
    setBusy(true)
    try {
      await api.post('/auth/password/change-required', { passwordChangeToken, currentPassword, newPassword })
      onDone()
    } catch (err) {
      const e2 = err as { body?: { message?: string }; message?: string }
      setError(e2.body?.message ?? e2.message ?? 'Could not change the password')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="forced-pw-title">
      <form onSubmit={(e) => void submit(e)} className="w-full max-w-md space-y-4 rounded-[6px] bg-white p-6 shadow-xl">
        <h2 id="forced-pw-title" className="text-lg font-semibold text-gray-900">Choose a new password</h2>
        <p className="text-sm text-gray-600">Your password was set by an administrator. Choose your own before you continue.</p>
        <label className="block text-sm font-medium text-gray-700">
          New password
          <input type="password" autoComplete="new-password" autoFocus value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            className="mt-1 w-full rounded-[3px] border border-gray-300 px-3 py-2" />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Confirm new password
          <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="mt-1 w-full rounded-[3px] border border-gray-300 px-3 py-2" />
        </label>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button type="submit" disabled={busy || newPassword === ''} className="rounded-[3px] bg-[#3366FF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  )
}
